/**
 * The subscription, said once at the top of every screen when it matters.
 *
 * Shown before anybody fills in a form rather than after the save is refused:
 * finding out that the trial ended by typing out an expense and losing it is
 * the experience this exists to prevent. Everyone sees it — the cashier is the
 * person about to record something — and only people who can act on billing
 * are sent to Billing.
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { subscriptionNotice, useSubscriptionAccess } from '../lib/subscription';
import { cn } from './ui';

export function SubscriptionBanner(): ReactNode {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const notice = subscriptionNotice(useSubscriptionAccess());
  // Billing says all of this already, in more detail, next to the way out.
  if (!notice || pathname.endsWith('/billing')) return null;

  const danger = notice.tone === 'danger';

  return (
    <div
      role={danger ? 'alert' : 'status'}
      className={cn(
        'mb-6 rounded-lg border px-4 py-3',
        danger ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50',
      )}
    >
      <div className="flex gap-2.5">
        <AlertTriangle
          className={cn('mt-0.5 size-4 shrink-0', danger ? 'text-red-700' : 'text-amber-700')}
          aria-hidden
        />
        <div className="min-w-0 flex-1 text-sm">
          <p className={cn('font-semibold', danger ? 'text-red-900' : 'text-amber-900')}>
            {notice.title}
          </p>
          <p className={cn('mt-0.5', danger ? 'text-red-800' : 'text-amber-800')}>
            {notice.body}
          </p>
          <p className="mt-1.5">
            {can('settings:read') ? (
              <Link
                to="/billing"
                className={cn(
                  'font-medium underline underline-offset-2',
                  danger ? 'text-red-900' : 'text-amber-900',
                )}
              >
                Choose a plan
              </Link>
            ) : (
              <span className={danger ? 'text-red-800' : 'text-amber-800'}>
                Ask the business owner to choose a plan.
              </span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
